/**
 * CSR wizard sections.
 *
 * Every wizard page owns exactly one backend section and only ever exchanges
 * that section's fields with the API:
 *
 *   GET   /studies/{studyId}/wizards/{section}   → { sectionData: {...} }
 *   PATCH /studies/{studyId}/wizards/{section}   ← the section's raw payload
 *
 * An empty `sectionData` object means "not answered yet", never an error.
 */
export const WIZARD_SECTIONS = {
  studyDesign: "study_design",
  efficacy: "efficacy",
  safety: "safety",
  standardPk: "standard_pk",
  comparativePk: "comparative_pk",
  bioavailability: "bioavailability",
  standardPd: "standard_pd",
  comparativePd: "comparative_pd",
  dataMapping: "data_mapping",
};

/**
 * Root-level keys of the submit payload that belong to each section.
 *
 * The keys listed here are the ones that survive `buildWizardPayload` — block
 * controls (qPkTable_2, qPdTableComp_3, …) have already been folded into their
 * `q*Analysis`/`q*Analyses` arrays by then, so they never need listing.
 */
export const WIZARD_SECTION_FIELDS = {
  [WIZARD_SECTIONS.studyDesign]: [
    "qStudyPhase",
    "qStudyPhaseNames",
    "qParticipantsName",
    "qParticipantsNameOther",
    "qComparative",
    "qDoseFinding",
    "qDoseFindingSafety",
    "qDoseFindingEfficacy",
    "qDoseFindingStatisticalEndpoint",
    "qTgNames",
    "qTgNamesDoseFinding",
    "qTgNamesComparison",
    "qTgNamesSingleArm",
    "qParticipantNumber",
    "qIntendedDuration",
    "qDemPopulation",
    "qCutOffsForDemographicsTables",
    "qSingleArmMultipleColumns",
  ],
  [WIZARD_SECTIONS.efficacy]: [
    "qIsEfficacy",
    "qEfficacyNonComp",
    "qEfficacyAnalytes",
    "qEfficacyPopulations",
    "qEfficacyAnalysisType",
    "qEfficacyAnalysisTypeNames",
    "qEfficacyBaseline",
    "qEfficacyTimepointNames",
    "qEfficacyKaplanMeierAnalytes",
    "qSecondaryEfficacyAnalytes",
    "qPrimaryEfficacy",
    "qPrimaryEfficacyAnalytes",
    "qPrimaryEfficacyAnalytesComparison",
    "qPrimaryEfficacyPopulation",
    "qPrimaryEfficacyAnalysisType",
    "qPrimaryEfficacyAnalysisTypeNames",
    "qPrimaryEfficacyBaseline",
    "qPrimaryEfficacyBaselineNonComp",
    "qPrimaryEfficacyComparisonNames",
    "qPrimaryEfficacyTimepointNames",
    "qPrimaryEfficacyTimepointNonComp",
  ],
  [WIZARD_SECTIONS.safety]: [
    "qSafetyBaseline",
    "qSafetyEndpoint",
    "qCutOffs",
    "qCutOffsForAbnormalLabTables",
    "qCutOffsForAdverseEventsTables",
    "qSubgroups",
    "qSubgroupClassification",
    "qSubgroupNames",
  ],
  // qPK is the umbrella "study has PK" flag. It is derived from the three PK
  // sub-sections but only ever written from Standard PK, the first PK page.
  [WIZARD_SECTIONS.standardPk]: ["qPK", "qPkStdEnabled", "qPkStdAnalysis"],
  [WIZARD_SECTIONS.comparativePk]: ["qPkCompEnabled", "qPkCompAnalysis"],
  [WIZARD_SECTIONS.bioavailability]: ["qPkRelBio", "qPkRelBioAnalyses"],
  [WIZARD_SECTIONS.standardPd]: ["qPdStdEnabled", "qPdStdAnalyses"],
  [WIZARD_SECTIONS.comparativePd]: ["qPdCompEnabled", "qPdCompAnalyses"],
};

/** The eight question pages, in wizard order. Data mapping is not one of them. */
export const WIZARD_QUESTION_SECTIONS = [
  WIZARD_SECTIONS.studyDesign,
  WIZARD_SECTIONS.efficacy,
  WIZARD_SECTIONS.safety,
  WIZARD_SECTIONS.standardPk,
  WIZARD_SECTIONS.comparativePk,
  WIZARD_SECTIONS.bioavailability,
  WIZARD_SECTIONS.standardPd,
  WIZARD_SECTIONS.comparativePd,
];

/**
 * Narrow a full wizard payload down to the fields owned by `section`.
 * Keys absent from the payload stay absent — sending `undefined` would tell
 * the backend the answer was cleared.
 */
export const pickSectionPayload = (section, wizardFormValue = {}) => {
  const fields = WIZARD_SECTION_FIELDS[section] || [];
  return fields.reduce((payload, field) => {
    if (Object.prototype.hasOwnProperty.call(wizardFormValue, field)) {
      payload[field] = wizardFormValue[field];
    }
    return payload;
  }, {});
};
